(() => {
  const drawer = document.getElementById('QuickAddDrawer');
  if (!drawer) return;

  const sizeOptionNames = (drawer.dataset.sizeOptions || '')
    .split(',')
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean);
  const colorOptionNames = (drawer.dataset.colorOptions || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  const optionsEl = drawer.querySelector('[data-quick-add-options]');
  const submitBtn = drawer.querySelector('[data-quick-add-submit]');
  const spinner = drawer.querySelector('.quick-add-drawer__spinner');
  const imageEl = drawer.querySelector('[data-quick-add-image]');
  const titleEl = drawer.querySelector('[data-quick-add-title]');
  const priceEl = drawer.querySelector('[data-quick-add-price]');

  const submitLabel = drawer.dataset.submitLabel || 'Add to cart';
  const soldOutLabel = drawer.dataset.soldOutLabel || window.variantStrings?.soldOut || 'Sold out';
  const unavailableLabel = drawer.dataset.unavailableLabel || soldOutLabel;
  const colorSwatchStyle = drawer.dataset.colorSwatchStyle || '';

  let currentCard = null;
  let productData = null;
  let selectedOptions = {};
  let isLoading = false;

  const parseJsonAttr = (el, attr) => {
    try {
      return JSON.parse(el.getAttribute(attr) || '[]');
    } catch {
      return [];
    }
  };

  const colorOptionNamesLower = colorOptionNames.map((name) => name.toLowerCase());

  const normalizeOptionValue = (value) => {
    if (value == null) return '';
    if (typeof value === 'object' && value.name != null) return String(value.name);
    return String(value);
  };

  const getOptionValues = (option) => (option.values || []).map(normalizeOptionValue);

  const parseCardData = (card) => ({
    handle: card.dataset.productHandle || '',
    id: card.dataset.productId || '',
    title: card.dataset.productTitle || '',
    image: card.dataset.productImage || '',
    price: Number(card.dataset.productPrice) || 0,
    compareAtPrice: Number(card.dataset.productCompareAtPrice) || 0,
    variants: parseJsonAttr(card, 'data-variants'),
    options: parseJsonAttr(card, 'data-options'),
    hasOnlyDefaultVariant: card.dataset.hasOnlyDefaultVariant === 'true',
  });

  const isColorOption = (name) => colorOptionNamesLower.includes(name.toLowerCase());

  // Option names are translated per storefront language ("Size" -> "Größe"), so
  // the settings.size_swatch_config list can never be complete. card-product.liquid
  // resolves the size option server-side (name list, then "the option that is not
  // the colour option", same negation the PDP picker uses) and publishes the winner
  // as data-size-option-name — trust that first, fall back to the name list for
  // cards that rendered no size selector at all.
  const isSizeOption = (name, card) => {
    const resolved = card?.dataset?.sizeOptionName;
    if (resolved) return resolved.toLowerCase() === name.toLowerCase();
    return sizeOptionNames.includes(name.toLowerCase());
  };

  const getRequiredOptions = (data) =>
    data.options.filter((option) => getOptionValues(option).length > 1);

  // PLP card swatches only preview imagery — they do not commit variant choice for buy-now.
  // Only an explicit desktop hover size pick counts as card-level selection.
  const cardHasSelection = (card, option) => {
    if (!isSizeOption(option.name, card)) return false;

    const selected = card.querySelector('.variant-selection-button.selected[data-size-value]');
    return Boolean(selected?.dataset.sizeValue);
  };

  const getCardPreselection = (card, data, { includeColor = true } = {}) => {
    const pre = {};

    for (const option of data.options) {
      if (includeColor && isColorOption(option.name)) {
        const active = card.querySelector('.swatch-button.active[data-option-value]');
        if (active?.dataset.optionValue) {
          pre[option.name] = active.dataset.optionValue;
        }
      } else if (isSizeOption(option.name, card)) {
        const selected = card.querySelector('.variant-selection-button.selected[data-size-value]');
        if (selected?.dataset.sizeValue) {
          pre[option.name] = selected.dataset.sizeValue;
        }
      }
    }

    return pre;
  };

  const getOptionKey = (position) => `option${position}`;

  const needsSelection = (card) => {
    const data = parseCardData(card);
    if (data.hasOnlyDefaultVariant) return false;
    const required = getRequiredOptions(data);
    return !required.every((option) => cardHasSelection(card, option));
  };

  const resolveVariant = (data, selections) => {
    if (!data?.variants?.length) return null;

    return data.variants.find((variant) =>
      data.options.every((option) => {
        const selected = selections[option.name];
        if (getOptionValues(option).length <= 1) return true;
        if (!selected) return false;
        return variant[getOptionKey(option.position)] === selected;
      })
    );
  };

  const getResolvedFromCard = (card, data) => {
    const selections = getCardPreselection(card, data, { includeColor: false });
    const required = getRequiredOptions(data);
    if (!required.every((option) => selections[option.name])) return null;
    return resolveVariant(data, selections);
  };

  const formatMoney = (cents) => {
    if (window.theme?.marketMoney?.format) {
      return window.theme.marketMoney.format(cents);
    }
    if (typeof window.formatMoney === 'function') {
      return window.formatMoney(cents, window.theme?.settings?.money_format);
    }
    return (cents / 100).toFixed(2);
  };

  const setPrice = (variant, fallbackData) => {
    if (!priceEl) return;

    const price = variant?.price ?? fallbackData.price;
    const compareAt = variant?.compare_at_price ?? fallbackData.compareAtPrice;
    const onSale = compareAt > price;

    if (onSale) {
      priceEl.innerHTML = `<span class="quick-add-drawer__product-price--sale">${formatMoney(price)}</span><span class="quick-add-drawer__product-price-compare">${formatMoney(compareAt)}</span>`;
    } else {
      priceEl.textContent = formatMoney(price);
    }
  };

  /* AAS-5048: swap the drawer image with a soft crossfade — the INCOMING
   * image preloads invisibly as an overlay over the untouched old one, fades
   * in once decoded, then the real node takes the src (same pattern as the
   * PLP tile crossfade in theme.js). A newer call removes pending overlays;
   * their commits no-op via isConnected. Fade duration mirrors the CSS
   * transition on .quick-add-drawer__image-fade. */
  const setDrawerImage = (src, alt) => {
    if (!imageEl || !src) return;
    const target = imageEl.dataset.fadeTarget || imageEl.src;
    if (target === src) return;
    if (alt) imageEl.alt = alt;

    const parent = imageEl.parentElement;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!parent || reduceMotion) {
      imageEl.src = src;
      delete imageEl.dataset.fadeTarget;
      return;
    }

    parent.querySelectorAll('.quick-add-drawer__image-fade').forEach((el) => el.remove());
    imageEl.dataset.fadeTarget = src;

    const overlay = imageEl.cloneNode();
    overlay.classList.add('quick-add-drawer__image-fade');
    overlay.removeAttribute('data-quick-add-image');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.opacity = '0';
    overlay.loading = 'eager';
    overlay.src = src;
    parent.appendChild(overlay);

    let settled = false;
    const commit = () => {
      if (settled || !overlay.isConnected) return;
      settled = true;
      requestAnimationFrame(() => {
        overlay.style.opacity = '1';
      });
      setTimeout(() => {
        if (!overlay.isConnected) return;
        imageEl.src = src;
        if (imageEl.dataset.fadeTarget === src) delete imageEl.dataset.fadeTarget;
        overlay.remove();
      }, 220);
    };
    if (overlay.decode) overlay.decode().then(commit, commit);
    setTimeout(commit, 1200);
  };

  const isValueAvailable = (data, optionName, value, selections) => {
    const option = data.options.find((item) => item.name === optionName);
    if (!option) return false;

    const trial = { ...selections, [optionName]: value };
    return data.variants.some(
      (variant) =>
        variant.available &&
        data.options.every((opt) => {
          const selected = trial[opt.name];
          if (getOptionValues(opt).length <= 1) return true;
          if (!selected) return true;
          return variant[getOptionKey(opt.position)] === selected;
        })
    );
  };

  /* AAS-5268: paint colour chips from option.swatches[value] (server-resolved in
   * quick-add-product-data via color-swatch-css). Do not scrape the card DOM —
   * cards with show_color_swatch:false have no swatch nodes. */
  const WHITE_SWATCH_CSS = new Set([
    '--swatch-color:#fff',
    '--swatch-color:#ffffff',
    '--swatch-color:white',
  ]);

  const isWhiteSwatch = (css) =>
    WHITE_SWATCH_CSS.has((css || '').toLowerCase().replace(/\s+/g, ''));

  const createColorSwatchButton = (value, swatchCss, isSelected, isSoldout) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'swatch-button swatch-button-color';
    button.dataset.optionValue = value;

    if (isSelected) button.classList.add('active');
    if (isSoldout) button.classList.add('is-soldout');
    if (colorSwatchStyle === 'round') button.classList.add('rounded-full');
    else if (colorSwatchStyle === 'rectangle') button.classList.add('swatch-color-rectangle');

    if (swatchCss) button.setAttribute('style', swatchCss);
    if (isWhiteSwatch(swatchCss)) button.classList.add('swatch-button-color--bordered');

    const label = document.createElement('span');
    label.className = 'sr-only';
    label.textContent = value;
    button.appendChild(label);

    return button;
  };

  /* AAS-5048: sizes (and any other non-colour option) render the PDP's
   * framed size picker verbatim — the radio + label.swatch-block markup from
   * snippets/product-variant-options.liquid inside the same wrapper classes
   * as product-variant-picker.liquid's button picker. The SHARED rules
   * (component-product-variant-picker.css, :is(.section-main-product,
   * .quick-add-drawer)) style both surfaces identically: frame, stacked
   * rings, crossed unavailable. An unavailable value gets the `disabled`
   * CLASS (never the attribute) and stays selectable, like the PDP —
   * resolving a sold-out variant flips the submit to "Sold out".
   * variant-picker__solid mirrors the store's PDP block style setting
   * (style: solid); a global drawer can't read a per-template block. */
  const buildChipGroup = (option, values) => {
    const fieldset = document.createElement('fieldset');
    fieldset.className = 'js product-form__input quick-add-drawer__sizes';

    // Equal-rows balancing, same modulo ladder as product-variant-picker.liquid.
    const count = values.length;
    let equalCols = 0;
    if (count >= 4 && count % 4 === 0) equalCols = 4;
    else if (count >= 3 && count % 3 === 0) equalCols = 3;
    else if (count >= 2 && count % 2 === 0) equalCols = 2;

    const picker = document.createElement('div');
    picker.className = 'variant-picker variant-picker--framed flex gap-x-1 gap-y-4 flex-wrap variant-picker__solid';
    if (equalCols > 0) {
      picker.classList.add('variant-picker--equal-rows');
      picker.style.setProperty('--equal-cols', String(equalCols));
    }
    fieldset.appendChild(picker);

    values.forEach((value, index) => {
      const available = isValueAvailable(productData, option.name, value, selectedOptions);
      const isSelected = selectedOptions[option.name] === value;
      const inputId = `QuickAddOpt-${option.position}-${index}`;

      const input = document.createElement('input');
      input.type = 'radio';
      input.id = inputId;
      input.name = `QuickAdd-${option.name}`;
      input.value = value;
      input.dataset.optionName = option.name;
      input.dataset.optionValue = value;
      if (!available) input.classList.add('disabled');
      input.checked = isSelected;
      input.addEventListener('change', () => handleOptionSelect(option.name, value));

      const chip = document.createElement('label');
      chip.className = 'swatch-block';
      chip.htmlFor = inputId;
      const chipValue = document.createElement('span');
      chipValue.className = 'swatch-block__value';
      chipValue.textContent = value;
      chip.appendChild(chipValue);
      const chipUnavailable = document.createElement('span');
      chipUnavailable.className = 'visually-hidden label-unavailable';
      chipUnavailable.textContent = unavailableLabel;
      chip.appendChild(chipUnavailable);

      picker.appendChild(input);
      picker.appendChild(chip);
    });

    return fieldset;
  };

  const renderOptions = () => {
    if (!optionsEl || !productData || !currentCard) return;

    optionsEl.innerHTML = '';

    for (const option of productData.options) {
      const values = getOptionValues(option);
      if (values.length <= 1) continue;

      const section = document.createElement('div');
      section.className = 'quick-add-drawer__option';
      section.dataset.optionName = option.name;

      const label = document.createElement('p');
      label.className = 'quick-add-drawer__option-label body-xs';
      label.textContent = option.label || option.name;
      section.appendChild(label);

      if (isSizeOption(option.name, currentCard)) {
        section.appendChild(buildChipGroup(option, values));
      } else if (isColorOption(option.name)) {
        section.classList.add('quick-add-drawer__option--color');

        const list = document.createElement('ul');
        list.className = 'quick-add-drawer__color-list list-unstyled';
        list.setAttribute('role', 'list');

        for (const value of values) {
          const available = isValueAvailable(productData, option.name, value, selectedOptions);
          const isSelected = selectedOptions[option.name] === value;
          const item = document.createElement('li');
          item.className = 'quick-add-drawer__color-item';

          const button = createColorSwatchButton(
            value,
            option.swatches?.[value] || '',
            isSelected,
            !available
          );
          button.dataset.optionName = option.name;
          button.setAttribute('aria-label', available ? value : `${value} — ${soldOutLabel}`);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

          button.addEventListener('click', () => handleOptionSelect(option.name, value));
          item.appendChild(button);
          list.appendChild(item);
        }

        section.appendChild(list);
      } else {
        // Non-colour, non-size options take the same PDP chips (that is how
        // the PDP renders them too).
        section.appendChild(buildChipGroup(option, values));
      }

      optionsEl.appendChild(section);
    }
  };

  const setSubmitLabel = (label) => {
    if (!submitBtn) return;
    submitBtn.textContent = label;
    submitBtn.setAttribute('aria-label', label);
  };

  const updateResolvedVariant = () => {
    const variant = resolveVariant(productData, selectedOptions);
    const required = getRequiredOptions(productData);
    const allSelected = required.every((option) => selectedOptions[option.name]);

    if (imageEl) {
      // AAS-5048: the image follows the COLOUR as soon as one is picked — a
      // full colour+size resolution is not required for imagery (picking a
      // colour without a size used to leave the previous image standing).
      // Imageless variants (FOUR JR Black / Graphic Pink) fall back to the
      // product image, mirroring the tile.
      let imageVariant = variant || null;
      if (!imageVariant) {
        const colorOption = productData.options.find(
          (o) => isColorOption(o.name) && getOptionValues(o).length > 1
        );
        const selectedColor = colorOption && selectedOptions[colorOption.name];
        if (selectedColor) {
          imageVariant = productData.variants.find(
            (v) => v[getOptionKey(colorOption.position)] === selectedColor
          ) || null;
        }
      }
      if (imageVariant) {
        setDrawerImage(imageVariant.image || productData.image, productData.title);
      }
    }

    setPrice(variant, productData);

    if (!submitBtn) return;

    if (allSelected && variant?.available) {
      submitBtn.disabled = false;
      setSubmitLabel(submitLabel);
    } else if (allSelected && variant && !variant.available) {
      submitBtn.disabled = true;
      setSubmitLabel(soldOutLabel);
    } else {
      submitBtn.disabled = true;
      setSubmitLabel(submitLabel);
    }
  };

  /* AAS-5048: update the existing option buttons in place instead of
   * rebuilding the DOM — persistent nodes let the CSS availability
   * transitions animate (background/strikethrough fades) exactly like the
   * PLP card's size row. Falls back to a full render when the buttons
   * aren't there yet. */
  const updateOptionStates = () => {
    const buttons = optionsEl ? optionsEl.querySelectorAll('[data-option-name][data-option-value]') : [];
    if (!buttons.length) {
      renderOptions();
      return;
    }
    buttons.forEach((button) => {
      const name = button.dataset.optionName;
      const value = button.dataset.optionValue;
      const available = isValueAvailable(productData, name, value, selectedOptions);
      const isSelected = selectedOptions[name] === value;
      if (button.tagName === 'INPUT') {
        // PDP picker radio (sizes + generic options): crossed via the
        // disabled CLASS, never the attribute — stays selectable.
        button.classList.toggle('disabled', !available);
        button.checked = isSelected;
      } else {
        // Colour swatch button.
        button.classList.toggle('is-soldout', !available);
        button.classList.toggle('active', isSelected);
        button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        button.setAttribute('aria-label', available ? value : `${value} — ${soldOutLabel}`);
      }
    });
  };

  const handleOptionSelect = (optionName, value) => {
    if (selectedOptions[optionName] === value) {
      delete selectedOptions[optionName];
    } else {
      selectedOptions[optionName] = value;
    }
    updateOptionStates();
    updateResolvedVariant();
  };

  const renderDrawer = () => {
    if (!productData) return;

    if (titleEl) titleEl.textContent = productData.title;
    if (imageEl) {
      // Fresh open: clear any crossfade leftovers from the previous product.
      imageEl.parentElement?.querySelectorAll('.quick-add-drawer__image-fade').forEach((el) => el.remove());
      delete imageEl.dataset.fadeTarget;
      imageEl.src = productData.image;
      imageEl.alt = productData.title;
    }

    setPrice(null, productData);
    renderOptions();
    updateResolvedVariant();
  };

  const setLoading = (loading) => {
    isLoading = loading;
    if (submitBtn) {
      submitBtn.classList.toggle('loading', loading);
      if (loading) {
        submitBtn.setAttribute('aria-disabled', 'true');
      } else {
        submitBtn.removeAttribute('aria-disabled');
        updateResolvedVariant();
      }
    }
    spinner?.classList.toggle('hidden', !loading);
  };

  const getCartDrawer = () => document.querySelector('cart-drawer');

  const appendCartDrawerSections = (formData, triggerEl) => {
    const cartDrawerEl = getCartDrawer();
    if (typeof cartDrawerEl?.getSectionsToRender !== 'function') return cartDrawerEl;

    try {
      formData.append(
        'sections',
        cartDrawerEl.getSectionsToRender().map((section) => section.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cartDrawerEl.setActiveElement?.(triggerEl);
    } catch (error) {
      formData.delete('sections');
      formData.delete('sections_url');
      console.warn('[quick-add-drawer] sections append failed', error);
    }

    return cartDrawerEl;
  };

  const renderCartDrawerContents = (cartDrawerEl, data) => {
    if (!cartDrawerEl || typeof cartDrawerEl.renderContents !== 'function') return false;

    const sections = cartDrawerEl.getSectionsToRender?.() || [];
    const hasAllSections = sections.every((section) => data.sections?.[section.id]);
    if (!hasAllSections) return false;

    try {
      cartDrawerEl.renderContents(data);
      return true;
    } catch (error) {
      console.error('[quick-add-drawer] cart drawer render failed', error);
      return false;
    }
  };

  /* GA4 item fields onto the cart line. Forms rendered in Liquid get these from
     snippets/datalayer-item-props.liquid as hidden inputs; this drawer builds its
     FormData in JS, so the same keys are copied from the originating card's
     data-gtm-item payload. Without them a quick-add line reaches
     add_to_cart / checkout / purchase with vendor-as-brand and no categories.
     Per-variant _item_variant (PIM size code) is not available here — the card
     payload describes its default variant — so it is deliberately omitted and the
     consumers fall back to the line's own size. */
  const appendDataLayerItemProps = (formData, triggerEl) => {
    try {
      // The trigger only resolves the card when the add came straight from it
      // (buyNowDirect). When a size has to be picked, the trigger is the
      // DRAWER's submit button, which lives outside the card — closest() then
      // returns null and every prop was silently dropped. That is the common
      // path for anything with sizes, so fall back to the card the drawer was
      // opened from.
      const root = triggerEl?.closest?.('[data-regios-dopp-generic-product-id]')
        || triggerEl?.closest?.('li, article, .card-wrapper')
        || currentCard?.closest?.('[data-regios-dopp-generic-product-id]')
        || currentCard;
      const node = root?.querySelector?.('[data-gtm-item]');
      if (!node) return;
      const item = JSON.parse(node.getAttribute('data-gtm-item'));
      if (!item) return;
      if (item.item_brand) formData.append('properties[_item_brand]', item.item_brand);
      ['2', '3', '4', '6', '7', '8'].forEach((n) => {
        const v = item['item_category' + n];
        if (v) formData.append('properties[_item_category' + n + ']', v);
      });
    } catch (e) { /* never block an add-to-cart over tracking */ }
  };

  const addToCart = async (variantId, triggerEl) => {
    if (!variantId || isLoading) return;

    setLoading(true);
    triggerEl?.classList?.add('loading');
    triggerEl?.setAttribute('aria-disabled', 'true');
    triggerEl?.querySelector('.loading__spinner')?.classList.remove('hidden');

    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');
    appendDataLayerItemProps(formData, triggerEl);

    const cartDrawerEl = appendCartDrawerSections(formData, triggerEl);
    const cartAddUrl = window.routes?.cart_add_url || '/cart/add';

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        if (response.ok && data.status && typeof publish === 'function') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'quick-add-drawer',
            message: data.message || data.description || window.cartStrings?.error,
          });
        }
        return;
      }

      drawer.close();

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'quick-add-drawer',
          productVariantId: String(variantId),
          cartData: data,
        });
      }

      if (!renderCartDrawerContents(cartDrawerEl, data)) {
        if (!document.body.classList.contains('smart-cart--enabled')) {
          window.location.href = window.routes?.cart_url || '/cart';
        }
      }
    } catch (error) {
      console.error('[quick-add-drawer] add to cart failed', error);
    } finally {
      setLoading(false);
      triggerEl?.classList?.remove('loading');
      triggerEl?.removeAttribute('aria-disabled');
      triggerEl?.querySelector('.loading__spinner')?.classList.add('hidden');

      const cartDrawerEl = getCartDrawer();
      if (cartDrawerEl?.classList.contains('is-empty')) {
        cartDrawerEl.classList.remove('is-empty');
      }
    }
  };

  const openDrawer = (card, trigger) => {
    currentCard = card;
    productData = parseCardData(card);
    selectedOptions = getCardPreselection(card, productData);
    renderDrawer();
    drawer.open(trigger);
  };

  const buyNowDirect = (card, trigger) => {
    const data = parseCardData(card);
    const variant =
      getResolvedFromCard(card, data) ||
      data.variants.find((item) => item.available) ||
      data.variants[0];

    if (!variant?.id) return;
    addToCart(variant.id, trigger);
  };

  drawer.quickAddOpen = openDrawer;
  drawer.quickAddNeedsSelection = needsSelection;

  const getQuickAddContext = (trigger) =>
    trigger.closest('.card-product') || trigger.closest('.stl-info__card');

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-quick-add-trigger]');
    if (!trigger) return;

    const card = getQuickAddContext(trigger);
    if (!card) return;

    event.preventDefault();

    if (needsSelection(card)) {
      openDrawer(card, trigger);
    } else {
      buyNowDirect(card, trigger);
    }
  });

  submitBtn?.addEventListener('click', () => {
    const variant = resolveVariant(productData, selectedOptions);
    if (!variant?.available) return;
    addToCart(variant.id, submitBtn);
  });
})();
